import React, { useEffect, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { faClose, faKey, faMailBulk, faPenToSquare, faUser } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { AdminEmployee } from "../../../Objects/AdminEmployee/AdminEmployee"
import { useCreateAdminEmployee } from "../../../hooks/AdminEmployee/useCreateAdminEmployee"
import { useSaveAdminEmployee } from "../../../hooks/AdminEmployee/useSaveAdminEmployee"
import { useAdminEmployeePermissions } from "../../../hooks/AdminEmployee/useAdminEmployeePermissions"
import { useError } from "../../../context/useErrorContext"
import { useMessage } from "../../../context/useMessageContext"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { useTranslation } from "react-i18next"
import { AdminEmployeePermissions } from "./AdminEmployeePermissions"
import { AdminChangePasswordPopup } from "./AdminChangePasswordPopup"
import { useResetPassword } from "../../../hooks/Components/useResetPassword"

interface AdminEmployeePupopProps {
  title: string
  employee?: AdminEmployee | null
  onSaved: () => void
  onClose: () => void
}

export const AdminEmployeePupop: React.FC<AdminEmployeePupopProps> = ({ title, employee, onSaved, onClose }) => {
  const { t } = useTranslation("adminEmployee")

  const [permissions, setPermissions] = useState<string[]>(employee?.permissions ?? [])

  const [isEditMode] = useState(!!employee)

  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false)

  const { setError } = useError()

  const { setMessage } = useMessage()

  const { HandleRecoverPassword } = useResetPassword()

  const {
    username,
    password,
    name,
    email,
    status,
    handleUsernameChange,
    handlePasswordChange,
    handleNameChange,
    handleEmailChange,
    handleStatusChange,
    handleSubmitForm,
    handleInitialValue,
  } = useCreateAdminEmployee()

  const { handleSaveAdminEmployee } = useSaveAdminEmployee()

  const { handleUpdatePermissions } = useAdminEmployeePermissions()

  useEffect(() => {
    handleInitialValue(employee)
  }, [employee])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    const data = handleSubmitForm()

    if (!data.username.trim() || !data.name.trim() || (!isEditMode && !data.password.trim())) {
      setError(t("general:label_title_error"), t("general:label_description_error"))

      return
    }

    if (!permissions.some((permission) => permission.trim().length > 0)) {
      setError(t("general:label_title_error"), t("label_permissions_required"))

      return
    }

    handleSaveAdminEmployee(data, permissions, () => {
      if (isEditMode && employee) {
        handleUpdatePermissions(employee.adminEmployeeId, permissions, () => {
          onClose()

          onSaved()
        })

        return
      }

      onClose()

      onSaved()
    })
  }

  const handleSendRecoverEmail = () => {
    if (!employee || !employee.email.trim()) {
      setError(t("general:label_title_error"), t("label_no_email_for_recover"))

      return
    }

    HandleRecoverPassword(employee.email, () => {
      setMessage(t("label_title_recover_email"), t("label_description_recover_email_success"))
    })
  }

  return (
    <div className={defaultStyle.overlay}>
      <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
        <div className={defaultStyle.alert}>
          <TitlePageResponsiveIconSmall
            title={title}
            icon={faPenToSquare}
            iconColor={THEME.colors.iconBGColor}
            children={
              <div className={defaultStyle.ButtonsActions}>
                {isEditMode && !employee?.isRoot && (
                  <div style={{ width: "100px", marginRight: "5px" }}>
                    <GenericDropDown
                      data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                      initialState={status}
                      handlerFuncion={handleStatusChange}
                      descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.select.status"
                    />

                    <GetSpace right={10} />
                  </div>
                )}

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.button.close"
                />
              </div>
            }
          />

          <GetSpace top={5} />

          {employee?.isRoot ? (
            <div className="p-6 text-center text-slate-600">{t("label_root_not_editable")}</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="p-[15px]">
                {!isEditMode && (
                  <>
                    <InputField
                      type="text"
                      placeholder={t("label_username_place_holder")}
                      onInvalidMessage={t("label_username_onInvalidMessage")}
                      icon={faMailBulk}
                      value={username}
                      onChange={handleUsernameChange}
                      required
                      descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.input.username"
                    />

                    <GetSpace top={10} />

                    <InputField
                      type="password"
                      placeholder={t("label_password_place_holder")}
                      onInvalidMessage={t("label_password_onInvalidMessage")}
                      icon={faKey}
                      value={password}
                      onChange={handlePasswordChange}
                      required
                      descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.input.password"
                    />

                    <GetSpace top={10} />
                  </>
                )}

                <InputField
                  type="text"
                  placeholder={t("label_name_place_holder")}
                  onInvalidMessage={t("label_name_onInvalidMessage")}
                  icon={faUser}
                  value={name}
                  onChange={handleNameChange}
                  required
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.input.name"
                />

                <GetSpace top={10} />

                <InputField
                  type="email"
                  placeholder={t("label_email_place_holder")}
                  onInvalidMessage={t("label_email_onInvalidMessage")}
                  icon={faMailBulk}
                  value={email}
                  onChange={handleEmailChange}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.input.email"
                />
              </div>

              <div className={defaultStyle.divisionBar}></div>

              <AdminEmployeePermissions value={permissions} onChange={setPermissions} />

              <div className={defaultStyle.divisionBar}></div>

              <div className={defaultStyle.buttonsController}>
                {isEditMode && employee && (
                  <>
                    <GetSpace right={5} />

                    <Button
                      label={t("button_change_password")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRemoveColor}
                      onClick={() => setIsChangePasswordOpen(true)}
                      descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.button.changePassword"
                    />

                    <GetSpace right={5} />

                    <Button
                      label={t("button_send_recover_email")}
                      typeButton="button"
                      type="primary"
                      size="medium"
                      bgColor={THEME.colors.buttonBGRegisterColor}
                      onClick={handleSendRecoverEmail}
                      descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.button.sendRecoverEmail"
                    />
                  </>
                )}

                <Button
                  label={t("buttons:button_save")}
                  typeButton="submit"
                  type="primary"
                  size="medium"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePupop.button.save"
                />
              </div>
            </form>
          )}
        </div>
      </Animate>

      {isChangePasswordOpen && employee && (
        <AdminChangePasswordPopup employee={employee} onClose={() => setIsChangePasswordOpen(false)} />
      )}
    </div>
  )
}
